import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { createApi } from "../../utils/createApi";
import {
  getComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";
import { Order } from "../Alerts/AlertsUtils/alertsInterfaces";
import { HeadCell } from "../Alerts/AlertsUtils/alertsInterfaces";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { IgnoreZonesPreviewDialog } from "./IgnoreZonesPreviewDialog";
import { formatSiteTimestamp } from "../ActionLogs/ActionLogsUtils/formatSiteTimestamp";
import {
  LineCrossingHistorySnapshot,
  LineCrossingPreviewDialog,
} from "./LineCrossingPreviewDialog";

export interface ProductActionLogRow {
  id: number | string;
  history_date: string;
  formatted_date: string;
  label_display_name: string;
  history_type: string;
  sensitivity_name: string;
  username: string;
  polygonal_zones: number[][][] | null;
  /** "metric" | "ignore_all" | "line_crossing"; line rows carry `line_crossing`. */
  record_type: string;
  line_crossing: LineCrossingHistorySnapshot | null;
}

const headCells: readonly HeadCell[] = [
  { id: "history_date", numeric: false, label: "Date" },
  { id: "label_display_name", numeric: false, label: "Product Metric" },
  { id: "history_type", numeric: false, label: "Action Type" },
  { id: "sensitivity_name", numeric: false, label: "Current Sensitivity" },
  { id: "username", numeric: false, label: "Updated by" },
];

// The endpoint pages at 100 rows by default, and the generic "Updated" rows
// (filtered out below) are common, so reading only page 1 dropped older ignore
// zone and sensitivity changes (SUP-834). Follow every page, up to the
// backend's 10,000-row merge cap.
const HISTORY_PAGE_SIZE = 500;
const HISTORY_MAX_PAGES = 20;

const fetchAllProductHistory = async (
  cameraId: string,
  isCancelled: () => boolean
): Promise<any[]> => {
  const AxiosUI = createApi(``);
  const results: any[] = [];
  for (let page = 1; page <= HISTORY_MAX_PAGES; page++) {
    const response: { data: { results?: any[]; next?: string | null } } =
      await AxiosUI.get(
        `metric/history/?camera=${cameraId}&page_size=${HISTORY_PAGE_SIZE}&page=${page}`
      );
    results.push(...(response.data.results || []));
    if (!response.data.next || isCancelled()) break;
  }
  return results;
};

const normalizeHistoryType = (historyType: string): string =>
  historyType.trim().toLowerCase();

const isLineCrossingRow = (row: ProductActionLogRow): boolean =>
  row.record_type === "line_crossing";

const isGenericUpdatedProductAction = (row: ProductActionLogRow): boolean =>
  normalizeHistoryType(row.history_type) === "updated";

function formatHistoryDate(
  dateString: string,
  siteTimezone: string | null
): string {
  return formatSiteTimestamp(new Date(dateString), siteTimezone);
}

function ProductActionLogsTableHead({
  orderBy,
  onRequestSort,
  order,
}: {
  orderBy: string;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
}) {
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              fontSize: "15px",
              fontFamily: "Mulish",
              fontWeight: "Bold",
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell
          align="left"
          sx={{
            fontSize: "15px",
            fontFamily: "Mulish",
            fontWeight: "Bold",
          }}
        >
          Zones / Lines
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

export const ProductActionLogsDialog = ({
  open,
  onClose,
  cameraId,
  siteTimezone = null,
}: {
  open: boolean;
  onClose: () => void;
  cameraId: string;
  siteTimezone?: string | null;
}) => {
  const [rows, setRows] = React.useState<ProductActionLogRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] =
    React.useState<keyof ProductActionLogRow>("history_date");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [selectedRowForIgnoreZones, setSelectedRowForIgnoreZones] =
    React.useState<ProductActionLogRow | null>(null);
  const [selectedRowForLineCrossing, setSelectedRowForLineCrossing] =
    React.useState<ProductActionLogRow | null>(null);

  React.useEffect(() => {
    if (!open || !cameraId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchAllProductHistory(cameraId, () => cancelled)
      .then((results: any[]) => {
        if (cancelled) return;
        const flattened: ProductActionLogRow[] = results.map((item: any) => ({
          id: item.id,
          history_date: item.history_date,
          formatted_date: formatHistoryDate(item.history_date, siteTimezone),
          label_display_name: item.label?.display_name ?? "",
          history_type: item.history_type ?? "",
          sensitivity_name: item.sensitivity?.name ?? "",
          username: item.username ?? "",
          polygonal_zones: Array.isArray(item.polygonal_zones)
            ? item.polygonal_zones
            : null,
          record_type: item.record_type ?? "metric",
          line_crossing: item.line_crossing ?? null,
        }));
        setRows(flattened);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.message ?? "Failed to load product action logs.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, cameraId, siteTimezone]);

  const filteredRows = React.useMemo(
    (): ProductActionLogRow[] =>
      rows.filter((row) => !isGenericUpdatedProductAction(row)),
    [rows]
  );

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredRows.length, rowsPerPage, page]);

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property as keyof ProductActionLogRow);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo((): ProductActionLogRow[] => {
    const comparator = getComparator(order, orderBy);
    const sorted = stableSort(
      filteredRows,
      comparator
    ) as unknown as ProductActionLogRow[];
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          minHeight: "80vh",
          fontFamily: "Mulish",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Mulish",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <span>Product Action Logs</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: "text.primary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography sx={{ fontFamily: "Mulish", color: "error.main", py: 2 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && (
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <TableContainer sx={{ maxHeight: "60vh" }}>
              <Table
                stickyHeader
                aria-label="product action logs table"
                sx={{ fontFamily: "Mulish" }}
              >
                <ProductActionLogsTableHead
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  order={order}
                />
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                        {row.formatted_date}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                        {row.label_display_name}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                        {row.history_type}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                        {row.sensitivity_name || "—"}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                        {row.username}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Mulish" }} align="left">
                        {isLineCrossingRow(row) ? (
                          <OrangeTextButton
                            onClick={() => setSelectedRowForLineCrossing(row)}
                            text="See Line Crossing"
                            disabled={false}
                          />
                        ) : (
                          <OrangeTextButton
                            onClick={() => setSelectedRowForIgnoreZones(row)}
                            text="See Ignore Zones"
                            disabled={false}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ fontFamily: "Mulish" }}
            />
          </Paper>
        )}
      </DialogContent>
      <IgnoreZonesPreviewDialog
        open={selectedRowForIgnoreZones !== null}
        onClose={() => setSelectedRowForIgnoreZones(null)}
        cameraId={cameraId}
        polygonalZones={selectedRowForIgnoreZones?.polygonal_zones ?? null}
      />
      <LineCrossingPreviewDialog
        open={selectedRowForLineCrossing !== null}
        onClose={() => setSelectedRowForLineCrossing(null)}
        cameraId={cameraId}
        lineCrossing={selectedRowForLineCrossing?.line_crossing ?? null}
      />
    </Dialog>
  );
};
